import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import RecommendationCardsAnimation from "../../components/RecommendationCardsAnimation";
import { Icon } from "../landing/icons";
import { useInView } from "../landing/sections";
import TrendsChart from "./TrendsChart";

const reveal = "rl-reveal";

const vars = (v: Record<string, string | number>) => v as CSSProperties;

function Head({ pill, title, lead }: { pill: string; title: ReactNode; lead?: string }) {
  return (
    <header className={`rl-head ${reveal}`}>
      <span className="rl-pill">{pill}</span>
      {title}
      {lead && <p className="rl-lead">{lead}</p>}
    </header>
  );
}

/* ---------------------------------------------------------------- Hero */

const FLOW = ["Choose your areas", "Set your current state", "Get your baseline roadmap"];

export function LiteHero({ children }: { children: ReactNode }) {
  return (
    <section className="rl-section rl-section--light rl-hero rl-lite-hero" id="reimagine-light" aria-labelledby="rl-lite-h1">
      <div className="rl-wrap rl-hero__grid">
        <div className={`rl-hero__copy ${reveal}`}>
          <span className="rl-eyebrow">Simplified &middot; Faster &middot; Lighter</span>
          <h1 className="rl-h1" id="rl-lite-h1">
            ReImagine <span className="rl-hl">Lite</span>
          </h1>
          <p className="rl-lead">
            Set your organization&rsquo;s current data + AI state and let the roadmap generator create a path to get
            started.
          </p>
          <ol className="rl-steps3">
            {FLOW.map((step, i) => (
              <li key={step}>
                <span>{String(i + 1).padStart(2, "0")}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
        <div className={`rl-genwrap ${reveal}`}>
          <span className="rl-blob" aria-hidden="true" />
          {children}
          <span className="rl-tag" style={{ right: "-3%", top: "-4%" }}>
            9 READINESS AREAS
          </span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- How it works */

const SOURCES = [
  { icon: "target", title: "Your current state", note: "From the areas you select" },
  { icon: "layers", title: "Our frameworks", note: "Proven Traxccel data + AI frameworks" },
  { icon: "trendUp", title: "Latest data + AI trends", note: "Maturing and emerging technologies" },
  { icon: "book", title: "Leading practices", note: "What works in your industry" },
];

const HORIZONS = [
  { label: "Now", s: "0%", l: "42%" },
  { label: "Next", s: "28%", l: "44%" },
  { label: "Later", s: "56%", l: "44%" },
];

const IN_PATHS = [
  "M0 40 C50 40 40 160 90 160",
  "M0 120 C50 120 40 160 90 160",
  "M0 200 C50 200 40 160 90 160",
  "M0 280 C50 280 40 160 90 160",
];

function Connector({ paths }: { paths: string[] }) {
  return (
    <svg className="rl-lcon" viewBox="0 0 90 320" preserveAspectRatio="none" aria-hidden="true">
      {paths.map((d, i) => (
        <g key={d}>
          <path d={d} className="rl-lcon__track" />
          <path d={d} className="rl-lcon__flow" />
          <circle r="3.5" className="rl-lcon__comet">
            <animateMotion dur="2.4s" begin={`${(i * 0.3).toFixed(1)}s`} repeatCount="indefinite" path={d} />
          </circle>
        </g>
      ))}
    </svg>
  );
}

export function LiteHow() {
  const [ref, active] = useInView<HTMLDivElement>(0.3);
  return (
    <section className="rl-section rl-dark" id="how" aria-labelledby="rl-lhow-h2">
      <div className="rl-wrap rl-stack">
        <Head
          pill="How it works"
          title={
            <h2 className="rl-h2" id="rl-lhow-h2">
              Smart technology that works as <span className="rl-hl">your consultant</span>
            </h2>
          }
          lead="It brings our frameworks, the latest data + AI trends and leading practices relevant to your industry."
        />
        <div className={reveal}>
          <div ref={ref} className={`rl-dia${active ? " is-active" : ""}`}>
            <ul className="rl-src">
              {SOURCES.map((s, i) => (
                <li key={s.title} className="rl-card-dark" style={vars({ "--d": `${(0.1 + i * 0.2).toFixed(1)}s` })}>
                  <span className="rl-ico">
                    <Icon name={s.icon} />
                  </span>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.note}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Connector paths={IN_PATHS} />
            <div className="rl-core">
              <i className="rl-core__halo" />
              <i className="rl-core__halo rl-core__halo--late" />
              <span className="rl-core__ico">
                <Icon name="sparkle" size={30} />
              </span>
              <b>AI consultant</b>
              <small>ReImagine Lite</small>
            </div>
            <Connector paths={["M0 160 H90"]} />
            <div className="rl-lout">
              <div className="rl-lout__head">
                <b>Baseline roadmap</b>
                <span>Ready</span>
              </div>
              <ul>
                {HORIZONS.map((h, i) => (
                  <li key={h.label}>
                    <span className="rl-lout__w">{h.label}</span>
                    <span className="rl-lout__tr">
                      <i style={vars({ "--s": h.s, "--l": h.l, "--k": i })} />
                    </span>
                  </li>
                ))}
              </ul>
              <p>A practical path to get started, tailored to your industry and organization.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- Framework */

const FRAMEWORK_POINTS = [
  { title: "Strategy to execution", body: "Connects your data + AI ambition to the work that delivers it." },
  { title: "Blueprint and guardrails", body: "Clear standards for governance, architecture and adoption." },
  {
    title: "Everything in sync",
    body: "Data, technology, people and processes move together to deliver real business impact.",
  },
];

export function LiteFramework() {
  return (
    <section className="rl-section rl-section--light" id="framework" aria-labelledby="rl-fw-h2">
      <div className="rl-wrap rl-fw">
        <div className={`rl-fw__copy ${reveal}`}>
          <span className="rl-pill">Our framework</span>
          <h2 className="rl-h2" id="rl-fw-h2">
            The intelligence-driven <span className="rl-hl">enterprise framework</span>
          </h2>
          <p className="rl-lead">The blueprint and guardrails behind every roadmap.</p>
          <ul className="rl-lst">
            {FRAMEWORK_POINTS.map((p) => (
              <li key={p.title}>
                <span className="rl-k">
                  <Icon name="check" size={14} strokeWidth={3} />
                </span>
                <div>
                  <b>{p.title}</b>
                  <p>{p.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={`rl-fw__art ${reveal}`}>
          <RecommendationCardsAnimation />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- Trends */

export function LiteTrends() {
  return (
    <section className="rl-section rl-dark" id="trends" aria-labelledby="rl-tr-h2">
      <div className="rl-wrap rl-trg">
        <div
          className={`rl-cht ${reveal}`}
          role="img"
          aria-label="Chart placing robotics, edge analytics and CoBots as maturing trends, and digital twins and synthetic data as emerging trends, by industry trend and organization readiness"
        >
          <TrendsChart />
        </div>
        <div className={`rl-trg__copy ${reveal}`}>
          <span className="rl-pill">Trends &amp; practices</span>
          <h2 className="rl-h2" id="rl-tr-h2">
            <span className="rl-hl">Trends &amp; practices</span> that match your readiness
          </h2>
          <p className="rl-lead">Practical, achievable adoption that fits where you are today.</p>
          <div className="rl-lg">
            <div className="rl-card-dark">
              <span className="rl-dot rl-dot--m" />
              <div>
                <h3>Maturing trends</h3>
                <p>Proven technologies your peers are already scaling.</p>
              </div>
            </div>
            <div className="rl-card-dark">
              <span className="rl-dot rl-dot--e" />
              <div>
                <h3>Emerging trends</h3>
                <p>Early technologies worth piloting before they go mainstream.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------ Baseline roadmap */

const LEVELS = [
  { n: "01", label: "Nascent", h: "34%" },
  { n: "02", label: "Emerging", h: "52%", here: true },
  { n: "03", label: "Advanced", h: "72%" },
  { n: "04", label: "Optimized", h: "92%" },
];

const NEXT_STEPS = [
  { when: "Now", now: true, title: "Assign data owners for core systems", area: "Data readiness" },
  { when: "Next", title: "Pilot one high-value AI use case", area: "AI readiness" },
  { when: "Later", title: "Scale governance across business units", area: "Risk & governance" },
];

export function LiteBaseline() {
  const [ref, active] = useInView<HTMLDivElement>(0.25);
  return (
    <section className="rl-section rl-section--light" id="roadmap" aria-labelledby="rl-rm-h2">
      <div className="rl-wrap rl-stack">
        <Head
          pill="Baseline roadmap"
          title={
            <h2 className="rl-h2" id="rl-rm-h2">
              A <span className="rl-hl">baseline roadmap</span> to elevate data + AI maturity
            </h2>
          }
          lead="A clear, practical starting point that shows where you stand and the right steps to take next."
        />
        <div className={reveal}>
          <div
            ref={ref}
            className={`rl-bpanel${active ? " is-active" : ""}`}
            role="img"
            aria-label="Example baseline roadmap: current maturity at emerging, with recommended next steps for now, next and later"
          >
            <div className="rl-bpanel__head">
              <b>
                <i>
                  <Icon name="map" size={16} />
                </i>
                Your baseline roadmap
              </b>
              <small>Example</small>
            </div>
            <div className="rl-bpa">
              <span className="rl-bk">Where you stand today</span>
              <div className="rl-bars">
                {LEVELS.map((l, i) => (
                  <div key={l.n} className={`rl-col${l.here ? " is-here" : ""}`} style={vars({ "--k": i })}>
                    <div className="rl-bar" style={vars({ "--h": l.h })}>
                      {l.here && <span className="rl-yah">You are here</span>}
                      <span className="rl-lv">{l.n}</span>
                    </div>
                    <b>{l.label}</b>
                  </div>
                ))}
              </div>
            </div>
            <div className="rl-bpa">
              <span className="rl-bk">Recommended next steps</span>
              <ul className="rl-acts">
                {NEXT_STEPS.map((s, i) => (
                  <li key={s.when} style={vars({ "--k": i })}>
                    <span className={`rl-wh${s.now ? " is-now" : ""}`}>{s.when}</span>
                    <div>
                      <b>{s.title}</b>
                      <small>{s.area}</small>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="rl-note">
                Ready to go deeper? Take your baseline into ReImagine 2.0 for a full, ROI-linked roadmap.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- CTA */

export function LiteCta({ onTalk }: { onTalk: () => void }) {
  return (
    <section className="rl-section rl-cta" aria-labelledby="rl-lcta-h2" style={{ minHeight: 0 }}>
      <div className="rl-wrap">
        <div className={`rl-band ${reveal}`}>
          <span aria-hidden="true" className="rl-ring rl-ring--1" />
          <span aria-hidden="true" className="rl-ring rl-ring--2" />
          <h2 className="rl-h2" id="rl-lcta-h2">
            Ready to go deeper with <span className="rl-hl">ReImagine 2.0</span>?
          </h2>
          <p className="rl-lead">
            Talk with our experts about a full, AI-powered maturity assessment and ROI-linked roadmap.
          </p>
          <div className="rl-btns">
            <button type="button" className="rl-btn rl-btn--primary" onClick={onTalk}>
              Talk with our experts
            </button>
            <Link to="/" className="rl-btn rl-btn--ghost">
              Explore ReImagine 2.0
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
